import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";

import Typography from "@mui/material/Typography";

import StyledModal from "components/Modal";
import StyledFormInput from "components/FormInput/FormInput";
import StyledFormMaskedInput from "components/FormInput/FormMaskedInput";
import StyledFormBooleanCheckbox from "components/FormInput/FormBooleanCheckbox";

import { useAppDispatch } from "store/hooks";
import {
  createIbanAccount,
  getIbanAccounts,
  EditableIbanAccount,
  updateIbanAccountByUid,
} from "store/features/ibanAccountSlice";

import { FormValues, validationSchema } from "./validation";

interface AccountFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  account?: EditableIbanAccount | null;
}

const AccountFormModal: React.FC<AccountFormModalProps> = (props) => {
  const { isOpen, account, onClose } = props;
  const dispatch = useAppDispatch();

  const methods = useForm<FormValues>({
    mode: "onSubmit",
    resolver: yupResolver(validationSchema),
    values: {
      organizationName: account?.organizationName || "",
      identificationCode: account?.identificationCode || "",
      iban: account?.iban ? account.iban : "",
      isSuspended: account?.isSuspended || false,
      publicName: account?.publicName || "",
    },
  });

  const onSubmit = async () => {
    await methods.handleSubmit(async (values: FormValues) => {
      if (account && account.ibanAccountUid) {
        await dispatch(
          updateIbanAccountByUid({
            body: {
              ...values,
              ibanAccountUid: account.ibanAccountUid,
            },
          })
        ).unwrap();
      } else {
        const { isSuspended: _, ...rest } = values;

        await dispatch(createIbanAccount({ body: rest })).unwrap();
      }

      await dispatch(getIbanAccounts()).unwrap();
      onCloseModal();
    })();
  };

  const onCloseModal = () => {
    onClose();
    methods.reset();
  };

  const renderTitle = () => {
    if (account && account.ibanAccountUid) {
      return "Редагувати реквізити";
    }
    return "Створити реквізити";
  };

  const renderActionBtnText = () => {
    if (account && account.ibanAccountUid) {
      return "Зберегти";
    }
    return "Створити реквізити";
  };

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onCloseModal}
      title={renderTitle()}
      actionBtnText={renderActionBtnText()}
      subtitle="Платіжні дані для ваших посилань"
      content={
        <>
          <FormProvider {...methods}>
            <StyledFormInput
              title="Публічна назва компанії (необов’язково)"
              name="publicName"
              fullWidth
              slotProps={{ input: { inputProps: { maxLength: 70 } } }}
            />
            <StyledFormInput
              title="Юридична назва компанії або ФОП"
              name="organizationName"
              fullWidth
              slotProps={{ input: { inputProps: { maxLength: 70 } } }}
            />
            <StyledFormInput
              title="ЄДРПОУ/РНОКПП"
              name="identificationCode"
              fullWidth
              slotProps={{ input: { inputProps: { maxLength: 13 } } }}
            />
            <Typography variant="subtitle2" color="grey800" mt={-1} mb={2}>
              Для розрахунків з юридичною особою (ТОВ\ТзОВ) потрібен ЄДРПОУ, а
              для розрахунків з фізичною особою (ФОП) потрібен Ідентифікаційний
              код (РНОКПП).
            </Typography>
            <StyledFormMaskedInput
              mask="UA 99 999999 99999 99999999999999"
              title="IBAN"
              name="iban"
              fullWidth
              slotProps={{ input: { inputProps: { inputMode: "numeric" } } }}
            />
            {account?.ibanAccountUid && (
              <StyledFormBooleanCheckbox
                name="isSuspended"
                label="Призупинити реквізит"
                helperText="Коли реквізит призупинено, його не можна обрати для створення або перегляду платіжних посилань. Це корисно, якщо ви тимчасово не можете приймати платежі чи досягли ліміту рахунку."
              />
            )}
          </FormProvider>
        </>
      }
      onAction={onSubmit}
    />
  );
};

export default AccountFormModal;
